import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { confirmer } from '../components/Confirm';
import { api, type AdminUser, type Environment } from '../api';

/** Date lisible en français ; l'ISO brut si non parsable. */
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('fr-CH', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function Admin() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [env, setEnv] = useState<Environment | null>(null);

  const reload = () => api.listUsers().then(setUsers).catch((e) => setErr(e.message));
  useEffect(() => { reload(); }, []);
  useEffect(() => { api.getEnvironment().then(setEnv).catch(() => setEnv(null)); }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      await api.createUser(username, password, isAdmin);
      setUsername(''); setPassword(''); setIsAdmin(false);
      reload();
    } catch (e: any) { setErr(e.message); }
  }

  async function resetPw(u: AdminUser) {
    const pw = prompt(`Nouveau mot de passe pour ${u.username} ?`);
    if (!pw) return;
    try { await api.resetPassword(u.id, pw); alert('Mot de passe réinitialisé'); }
    catch (e: any) { alert('Erreur: ' + e.message); }
  }

  async function del(u: AdminUser) {
    if (!await confirmer(`Supprimer ${u.username} ?`)) return;
    try { await api.deleteUser(u.id); reload(); }
    catch (e: any) { alert('Erreur: ' + e.message); }
  }

  return (
    <div className="admin-page">
      <p><Link to="/">← Retour</Link></p>
      <h1>Administration</h1>

      <h2 className="env-title">Environnement</h2>
      {!env ? (
        <p className="env-loading">Chargement…</p>
      ) : (
        <div className="env-grid">
          <section className="env-card">
            <h3>
              Odoo
              <span className={env.odoo.reachable ? 'env-dot ok' : 'env-dot ko'}>
                {env.odoo.reachable ? 'joignable' : 'injoignable'}
              </span>
            </h3>
            <dl>
              <dt>Base</dt><dd><code>{env.odoo.db ?? '—'}</code></dd>
              <dt>Version</dt><dd>{env.odoo.version ?? '—'}</dd>
              <dt>URL</dt>
              <dd>
                {env.odoo.url
                  ? <a href={env.odoo.url} target="_blank" rel="noreferrer">{env.odoo.url.replace(/^https?:\/\//, '')}</a>
                  : '—'}
              </dd>
              <dt>Compte</dt><dd>{env.odoo.login ?? '—'}</dd>
              <dt>Champ stockable</dt><dd><code>{env.odoo.storableField ?? '—'}</code></dd>
              <dt>Activité « à valider »</dt><dd>id {env.odoo.toValidateActivityTypeId}</dd>
              <dt>Mot de passe PDF</dt>
              <dd>{env.odoo.pdfPasswordConfigured
                ? <span className="env-note">renseigné (non vérifié)</span>
                : <span className="env-warn">absent — boutons PDF désactivés</span>}</dd>
            </dl>
            {env.odoo.error && <p className="env-warn">{env.odoo.error}</p>}
          </section>

          <section className="env-card">
            <h3>
              n8n
              <span className={env.n8n.reachable ? 'env-dot ok' : 'env-dot ko'}>
                {env.n8n.reachable ? 'joignable' : 'injoignable'}
              </span>
            </h3>
            <dl>
              <dt>Instance</dt>
              <dd>
                {env.n8n.domain
                  ? <a href={`https://${env.n8n.domain}`} target="_blank" rel="noreferrer">{env.n8n.domain}</a>
                  : '—'}
              </dd>
              <dt>Workflows</dt>
              <dd>{env.n8n.workflowsTotal === null
                ? '—'
                : `${env.n8n.workflowsActive} actifs sur ${env.n8n.workflowsTotal}`}</dd>
              <dt>Clé API</dt>
              <dd>
                {!env.n8n.apiKeyConfigured ? <span className="env-warn">absente</span>
                  : env.n8n.apiKeyExpired ? <span className="env-warn">expirée le {fmtDate(env.n8n.apiKeyExpiresAt)}</span>
                  : env.n8n.apiKeyExpiresAt ? <>expire le {fmtDate(env.n8n.apiKeyExpiresAt)}</>
                  : <span className="env-note">sans expiration</span>}
              </dd>
            </dl>
            {env.n8n.error && <p className="env-warn">{env.n8n.error}</p>}
          </section>
        </div>
      )}

      <h2 className="env-title">Utilisateurs</h2>
      {err && <div style={{ color: '#c00' }}>{err}</div>}
      <table>
        <thead><tr><th>ID</th><th>Username</th><th>Admin</th><th>Créé</th><th></th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td data-label="ID">{u.id}</td>
              <td className="cell-title">{u.username}</td>
              <td data-label="Admin">{u.is_admin ? '✓' : ''}</td>
              <td data-label="Créé">{u.created_at}</td>
              <td>
                <button onClick={() => resetPw(u)}>reset pw</button>{' '}
                <button onClick={() => del(u)}>supprimer</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <form onSubmit={create}>
        <input placeholder="username" value={username} onChange={(e) => setUsername(e.target.value)} required />
        <input type="password" placeholder="password (min 8)" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
        <label><input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} /> admin</label>
        <button>Créer</button>
      </form>
    </div>
  );
}
